import React, { useMemo } from "react";

/**
 * Renders the agency's waiting queue grouped by category.
 * Highlights categories allowed by the connected counter so the agent
 * immediately knows what they can call.
 */
export function WaitingQueueByCategory({ waiting, allowedCategoryCodes }) {
  const groups = useMemo(() => {
    const map = new Map();
    waiting.forEach((tk) => {
      const k = tk.category_code || "UNK";
      if (!map.has(k)) {
        map.set(k, {
          code: k,
          label: tk.category_label || k,
          color: tk.color || "#94A3B8",
          priority: tk.priority_level ?? 0,
          tickets: [],
        });
      }
      map.get(k).tickets.push(tk);
    });
    return Array.from(map.values()).sort((a, b) => b.priority - a.priority);
  }, [waiting]);

  const allowed = useMemo(
    () => new Set(allowedCategoryCodes || []),
    [allowedCategoryCodes],
  );
  const hasRestrictions = (allowedCategoryCodes || []).length > 0;

  return (
    <div className="space-y-3 overflow-y-auto flex-1 no-scrollbar" data-testid="waiting-by-category">
      {groups.map((g) => {
        const isAllowed = !hasRestrictions || allowed.has(g.code);
        return (
          <div
            key={g.code}
            className={`rounded-xl border p-3 ${
              isAllowed ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60"
            }`}
            data-testid={`waiting-group-${g.code}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: g.color }} />
                <div className="font-display font-bold text-slate-900">{g.label}</div>
                {!isAllowed && (
                  <span className="ml-1 text-[10px] uppercase tracking-widest text-slate-400">non autorisé</span>
                )}
              </div>
              <div className="text-xs font-mono text-slate-500" data-testid={`waiting-group-${g.code}-count`}>
                {g.tickets.length}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {g.tickets.slice(0, 8).map((t) => (
                <span
                  key={t.id}
                  className="px-2.5 py-1 rounded-md font-mono font-bold text-sm tabular-nums"
                  style={{ background: `${g.color}1A`, color: g.color }}
                >
                  {t.full_ticket_code}
                </span>
              ))}
              {g.tickets.length > 8 && (
                <span className="px-2.5 py-1 rounded-md font-mono text-xs text-slate-400">
                  +{g.tickets.length - 8}
                </span>
              )}
            </div>
          </div>
        );
      })}
      {groups.length === 0 && (
        <div className="text-center text-slate-400 py-12 text-sm" data-testid="waiting-empty">
          Aucun ticket en attente
        </div>
      )}
    </div>
  );
}
